import { useRef, useEffect, useReducer } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useModalContext } from './useModalContext';
import { useDeviceDetection } from '../DynamicWrapper/hooks/useDeviceDetection';
import { computeAllPositions, getViewportHeight } from './utils/modalPositioning';
import ModalLayer from './ModalLayer';
import './Modal.css';

const _resetWrapperStyles = (wrapper) => {
  wrapper.style.transform = '';
  wrapper.style.overflow = '';
  wrapper.style.transition = '';
  wrapper.style.justifySelf = '';
  wrapper.style.transformOrigin = '';
  document.body.style.overflow = '';
};

const _computeMobileWrapperTransform = (wrapper, wrapperScale, modalStack) => {
  const viewportH = getViewportHeight();
  const wrapperH = wrapper.offsetHeight;
  const naturalVisualTop = (viewportH - 15) - wrapperH * wrapperScale;

  const visibleModals = modalStack.filter(m => m.animationState !== 'exiting');
  const topModalIndex = modalStack.length - 1;
  /* istanbul ignore next -- @preserve defensive: function only called when modalStack is non-empty */
  const topModalEl = topModalIndex >= 0
    ? document.getElementById(`dynamic-modal-${topModalIndex}`)
    : null;
  const topModalH = topModalEl ? topModalEl.clientHeight : 0;

  const stackSize = visibleModals.length;
  const staircaseGap = 60 - 15 * (stackSize + 1);
  const modalTop = topModalH > 0 ? (viewportH - 16 - topModalH) : viewportH;
  const requiredWrapperTop = modalTop - staircaseGap;

  if (naturalVisualTop > requiredWrapperTop) {
    const shift = requiredWrapperTop - naturalVisualTop;
    return `translateY(${shift}px) scale(${wrapperScale})`;
  }
  return `scale(${wrapperScale})`;
};

const _applyWrapperScaling = (wrapper, { isDesktop, wrapperScale, modalStack }) => {
  wrapper.style.overflow = 'visible';
  wrapper.style.transition = 'height 0.65s, transform 0.65s';

  if (isDesktop) {
    wrapper.style.transformOrigin = 'top';
    wrapper.style.transform = `translateX(-50%) scale(${wrapperScale})`;
  } else {
    wrapper.style.transformOrigin = 'center bottom';
    wrapper.style.justifySelf = 'anchor-center';
    wrapper.style.transform = _computeMobileWrapperTransform(wrapper, wrapperScale, modalStack);
  }

  document.body.style.overflow = 'hidden';
};

const ModalRenderer = ({ closeIconSrc, translateTitle, forcePortal = false }) => {
  const { modalStack, closeModal, updateModal, wrapperScale, activeCount } = useModalContext();
  const isDesktop = useDeviceDetection();
  const modalRefsMap = useRef({});
  const observerRef = useRef(null);
  const portalRef = useRef(null);
  const [updateTick, forceUpdate] = useReducer((n) => n + 1, 0);

  // Mobile modals always render outside the wrapper via portal.
  // Desktop also goes through the portal when `forcePortal` is set — required
  // when the ModalProvider lives ABOVE the wrapper (e.g. You-host), since
  // inline rendering would parent the modal at the document root and the
  // `width: calc(100% - 32px)` would expand to the full viewport.
  const usePortal = forcePortal || !isDesktop;

  const getModalRef = (index, el) => {
    modalRefsMap.current[index] = el;
  };

  // Create portal container as a sibling of .wrapper (outside it, but inside the same
  // stacking context so the header element's z-index can sit above it).
  useEffect(() => {
    let el = document.getElementById('dw-modal-portal');
    if (!el) {
      const wrapper = document.getElementsByClassName('wrapper')[0];
      el = document.createElement('div');
      el.id = 'dw-modal-portal';
      el.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:9999;pointer-events:none;';
      if (wrapper?.parentNode) {
        // Insert after the wrapper, keeping it in the same DOM context as the header
        wrapper.parentNode.insertBefore(el, wrapper.nextSibling);
      } else {
        document.body.appendChild(el);
      }
    }
    portalRef.current = el;
    forceUpdate();
  }, []);

  const getWrapperHeight = () => {
    const wrapper = document.getElementsByClassName('wrapper')[0];
    return wrapper?.clientHeight || 600;
  };

  // Apply wrapper scaling effect when modals are open
  useEffect(() => {
    const wrapper = document.getElementsByClassName('wrapper')[0];
    if (!wrapper) return;

    if (activeCount === 0) {
      _resetWrapperStyles(wrapper);
      return;
    }

    _applyWrapperScaling(wrapper, { isDesktop, wrapperScale, modalStack });
  }, [activeCount, wrapperScale, isDesktop, modalStack, updateTick]);

  // visualViewport listener: position portal modal above virtual keyboard on mobile
  useEffect(() => {
    const vv = window.visualViewport;
    if (isDesktop || !vv) return;

    document.documentElement.style.setProperty('--dw-vv-height', `${vv.height}px`);

    let isFocused = false;
    let focusTimer = null;
    let blurTimer = null;
    const STABLE_DELAY = 200;

    const getFixedAnchor = () => {
      const ruler = document.createElement('div');
      ruler.style.cssText = 'position:fixed;bottom:0;left:0;width:1px;height:1px;pointer-events:none;opacity:0;';
      document.body.appendChild(ruler);
      const anchor = ruler.getBoundingClientRect().bottom;
      ruler.remove();
      return anchor;
    };

    const applyShift = () => {
      if (!isFocused || vv.height >= window.innerHeight * 0.85) {
        document.documentElement.style.removeProperty('--dw-modal-bottom');
        return;
      }
      const bottom = Math.round(getFixedAnchor() - (vv.offsetTop || 0) - vv.height + 16);
      document.documentElement.style.setProperty('--dw-modal-bottom', `${bottom}px`);
    };

    const handleViewportResize = () => {
      document.documentElement.style.setProperty('--dw-vv-height', `${vv.height}px`);
      forceUpdate();
      if (isFocused) {
        clearTimeout(focusTimer);
        focusTimer = setTimeout(applyShift, STABLE_DELAY);
      }
    };

    const handlePortalFocusIn = (e) => {
      const portal = document.getElementById('dw-modal-portal');
      if (!portal?.contains(e.target)) return;
      clearTimeout(blurTimer);
      isFocused = true;
      clearTimeout(focusTimer);
      focusTimer = setTimeout(applyShift, STABLE_DELAY);
    };

    const handlePortalFocusOut = (e) => {
      const portal = document.getElementById('dw-modal-portal');
      if (!portal?.contains(e.target)) return;
      clearTimeout(focusTimer);
      blurTimer = setTimeout(() => {
        if (!portal?.contains(document.activeElement)) {
          isFocused = false;
          document.documentElement.style.removeProperty('--dw-modal-bottom');
          document.documentElement.style.setProperty('--dw-vv-height', `${vv.height}px`);
        }
      }, 150);
    };

    vv.addEventListener('resize', handleViewportResize);
    document.addEventListener('focusin', handlePortalFocusIn);
    document.addEventListener('focusout', handlePortalFocusOut);

    return () => {
      vv.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('focusin', handlePortalFocusIn);
      document.removeEventListener('focusout', handlePortalFocusOut);
      clearTimeout(focusTimer);
      clearTimeout(blurTimer);
      document.documentElement.style.removeProperty('--dw-vv-height');
      document.documentElement.style.removeProperty('--dw-modal-bottom');
    };
  }, [isDesktop]);

  // ResizeObserver: recalculate when the topmost modal resizes (mobile)
  useEffect(() => {
    /* istanbul ignore next -- @preserve defensive: React cleanup always nulls ref before effect body */
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (isDesktop || activeCount === 0) return;

    const topModalIndex = modalStack.length - 1;
    /* istanbul ignore next -- @preserve defensive: effect returns early when activeCount === 0 */
    const topModalEl = topModalIndex >= 0
      ? document.getElementById(`dynamic-modal-${topModalIndex}`)
      : null;

    if (!topModalEl) return;

    observerRef.current = new ResizeObserver(() => {
      forceUpdate();
    });
    observerRef.current.observe(topModalEl);

    return () => {
      /* istanbul ignore next -- @preserve defensive: cleanup only registered after observer is created */
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [activeCount, isDesktop, modalStack.length]);

  // Compute all modal Y positions at once
  const computedPositionsRef = useRef([]);
  const translateFn = (index) => {
    const visibleModals = modalStack.filter((m) => m.animationState !== 'exiting');
    const stackSize = visibleModals.length;
    const wrapperHeight = getWrapperHeight();

    const visibleIndices = [];
    modalStack.forEach((m, i) => {
      if (m.animationState !== 'exiting') visibleIndices.push(i);
    });

    const modalHeights = visibleIndices.map((vi) => {
      const el = modalRefsMap.current[vi];
      return el ? el.clientHeight : 0;
    });

    const positions = computeAllPositions(stackSize, isDesktop, wrapperHeight, wrapperScale, modalHeights);
    computedPositionsRef.current = positions;

    const visiblePos = visibleIndices.indexOf(index);
    // On mobile portal, the top modal is positioned by CSS (bottom: var(--dw-modal-bottom, 16px));
    // translateY from the stacking formula would push it off-screen in this coordinate system.
    if (!isDesktop && usePortal && visiblePos === stackSize - 1) return 0;
    return visiblePos >= 0 ? positions[visiblePos] : 0;
  };

  const visibleStackSize = modalStack.filter((m) => m.animationState !== 'exiting').length;

  const processedStack = modalStack.map((modal) => ({
    ...modal,
    title: modal.title && translateTitle ? translateTitle(modal.title) : (modal.title || ''),
  }));

  // Find the top visible (non-exiting) modal — overlay renders right before it
  const topVisibleIndex = (() => {
    for (let i = processedStack.length - 1; i >= 0; i--) {
      if (processedStack[i].animationState !== 'exiting') return i;
    }
    return -1;
  })();

  const modalContent = (
    <>
      {processedStack.map((modal, index) => (
        <ModalLayer
          key={modal._id}
          modal={modal}
          index={index}
          stackSize={visibleStackSize}
          isDesktop={isDesktop}
          wrapperHeight={getWrapperHeight()}
          getModalRef={getModalRef}
          onClose={closeModal}
          onUpdate={updateModal}
          translateFn={translateFn}
          closeIconSrc={closeIconSrc}
          usePortal={usePortal}
          showOverlay={index === topVisibleIndex}
        />
      ))}
    </>
  );

  // Mobile: render via portal (outside wrapper, not affected by wrapper's transform)
  // Desktop: render inline (inside wrapper, scales compensate for wrapper transform)
  if (usePortal && portalRef.current) {
    return createPortal(modalContent, portalRef.current);
  }

  return modalContent;
};

ModalRenderer.propTypes = {
  closeIconSrc: PropTypes.string,
  translateTitle: PropTypes.func,
  forcePortal: PropTypes.bool,
};

export default ModalRenderer;
